import React, { useMemo, useCallback, useState } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { useModalStore } from '@/store';
import { PLUS_COLORS } from '@/constants/Colors';
import { PLUS_FEATURES, PLUS_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';

const createStyles = (colors) => StyleSheet.create({
  container: {
    ...(Platform.OS === 'macos' && { width: '100%', flexShrink: 0 }),
    backgroundColor: PLUS_COLORS.backgroundColorPurple,
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: PLUS_COLORS.textColorPurple,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 12,
  },
  title: {
    color: colors.text_color_primary,
    flex: 1,
  },
  description: {
    marginBottom: 16,
    lineHeight: 22,
  },
  benefitsList: {
    gap: 10,
    marginBottom: 16,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  benefitIcon: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  benefitTextContainer: {
    flex: 1,
  },
  buttonContainer: {
    marginTop: 4,
  },
});

function PlusUpsellBanner({ onClose }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const handleUpgrade = useCallback(async () => {
    setIsLoading(true);
    try {
      onClose?.();
      closeModal();
      if (Platform.OS === 'ios') {
        setTimeout(() => router.push('/(auth)/paywall'), 100);
      } else {
        setTimeout(() => openModal('PricingModal'), 100);
      }
    } finally {
      setIsLoading(false);
    }
  }, [openModal, onClose, closeModal, router]);

  const benefits = PLUS_FEATURES;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Icon name="sparkles" size={18} color={PLUS_COLORS.textColorPurple} solid style={{ marginTop: 3 }} />
        <Text size="l" weight="bold" style={styles.title}>
          {`FREE MESSAGES + ${PLUS_MONTHLY_CREDITS_DISPLAY} MONTHLY CREDITS`}
        </Text>
      </View>

      <View style={styles.benefitsList}>
        {benefits.map((f) => (
          <View key={f.icon} style={styles.benefitItem}>
            <View style={styles.benefitIcon}>
              <Icon name={f.icon} size={16} color={PLUS_COLORS.textColorPurple} solid />
            </View>
            <View style={styles.benefitTextContainer}>
              <Text size="n" weight="bold">{f.boldText}</Text>
              <Text size="s" color="secondary">{f.text}</Text>
            </View>
          </View>
        ))}
      </View>

      <View style={styles.buttonContainer}>
        <Button
          variant="plus"
          text={(Platform.OS === 'ios' || Platform.OS === 'macos') ? "Upgrade to Plus" : "Upgrade to Plus - $16.67/mo"}
          prependIcon="sparkles"
          onPress={handleUpgrade}
          isLoading={isLoading}
          disabled={isLoading}
          isFullWidth={Platform.OS !== 'macos'}
        />
        {Platform.OS === 'macos' && <Text size="s" color="secondary" style={{ textAlign: 'center', marginTop: 8 }}>$16.67/mo billed annually</Text>}
      </View>
    </View>
  );
}

export default React.memo(PlusUpsellBanner);
